import { ApiService } from '../../../types/api';
import type { ApiHandlerContract } from '../../../types/handlers/api';
import type { PriceListApiResponse, PriceListEdit } from '../types';

/**
 * A service contract to interact with a specific price list via the SparkLayer API.
 */
export interface PriceListApiContract {
  get(): Promise<PriceListApiResponse>;
  clear(): Promise<void>;
  delete(): Promise<void>;
  update(payload: PriceListEdit): Promise<PriceListApiResponse>;
  uploadCsv(csv: string): Promise<void>;
  exportPricingCsv(): Promise<string>;
}

/**
 * A service to interact with a specific price list via the SparkLayer API.
 */
export class PriceListApi extends ApiService implements PriceListApiContract {
  constructor(
    api: ApiHandlerContract,
    private slug: string,
  ) {
    super(api);
  }

  public get(): Promise<PriceListApiResponse> {
    return this.api.proxy({
      method: 'GET',
      path: '/v1/price-lists/{slug}',
      params: { slug: encodeURIComponent(this.slug) },
    });
  }

  public clear(): Promise<void> {
    return this.api.proxy({
      method: 'DELETE',
      path: '/v1/price-lists/{slug}/pricing',
      params: { slug: encodeURIComponent(this.slug) },
    });
  }

  public delete(): Promise<void> {
    return this.api.proxy({
      method: 'DELETE',
      path: '/v1/price-lists/{slug}',
      params: { slug: encodeURIComponent(this.slug) },
    });
  }

  public update(payload: PriceListEdit): Promise<PriceListApiResponse> {
    return this.api.proxy<PriceListApiResponse>({
      method: 'PATCH',
      path: `/v1/price-lists/{slug}`,
      payload,
      params: { slug: encodeURIComponent(this.slug) },
    });
  }

  public uploadCsv(csv: string): Promise<void> {
    return this.api.proxy({
      path: '/v1/price-lists/{slug}/pricing',
      method: 'PATCH',
      payload: csv,
      params: { slug: encodeURIComponent(this.slug) },
      contentType: 'text/csv',
    });
  }

  public exportPricingCsv(): Promise<string> {
    return this.api.proxy<string>({
      method: 'GET',
      path: '/v1/price-lists/{slug}/pricing',
      params: { slug: encodeURIComponent(this.slug) },
      contentType: 'text/csv',
    });
  }
}
